import { BRAND_CONFIGS, BrandKey, DEFAULT_BRAND_KEY, getBrandConfig, isBrandKey } from '../config/brands';

export const BRAND_ROUTE_PREFIX = '/brands';

/**
 * Normalize a relative route fragment so brand path generation stays predictable.
 * Why this matters: the selector needs to preserve the current page when users
 * switch brands, and that only works if route fragments are cleaned consistently.
 */
export const normalizeBrandRelativePath = (value = ''): string => {
  const trimmed = value.trim();
  if (!trimmed || trimmed === '/') {
    return '';
  }

  return trimmed.replace(/^\/+/, '').replace(/\/+$/, '');
};

/**
 * Build a route inside a brand namespace.
 * Why this matters: all internal navigation should flow through one helper so the
 * app never mixes unprefixed legacy routes with brand-specific routes.
 */
export const getBrandPath = (brandKey: BrandKey, relativePath = ''): string => {
  const normalizedPath = normalizeBrandRelativePath(relativePath);
  const basePath = `${BRAND_ROUTE_PREFIX}/${brandKey}`;

  return normalizedPath ? `${basePath}/${normalizedPath}` : basePath;
};

/**
 * Parse the active brand key from the current pathname.
 * Why this matters: route-based brand state lets the app stay bookmarkable and
 * keeps the selected brand in sync across refreshes and direct links.
 */
export const getBrandKeyFromPathname = (pathname: string): BrandKey | null => {
  const segments = pathname.split('/').filter(Boolean);
  const [, maybeBrandKey] = segments;

  return segments[0] === 'brands' && isBrandKey(maybeBrandKey) ? maybeBrandKey : null;
};

/**
 * Remove the `/brands/<brandKey>` prefix from a pathname.
 * Why this matters: switching brands should keep users on the same page instead
 * of always dumping them back on the landing screen.
 */
export const getBrandRelativePathFromPathname = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean);

  if (segments[0] !== 'brands' || !isBrandKey(segments[1])) {
    return normalizeBrandRelativePath(pathname);
  }

  return normalizeBrandRelativePath(segments.slice(2).join('/'));
};

/**
 * Rewrite the current pathname to another brand while preserving the sub-route.
 * Why this matters: this powers the new multi-brand switcher in the left rail.
 */
export const switchBrandInPathname = (pathname: string, targetBrandKey: BrandKey): string =>
  getBrandPath(targetBrandKey, getBrandRelativePathFromPathname(pathname));

/**
 * Resolve a brand from hostname, route, or fallback default.
 * Why this matters: this makes the app resilient when opened directly on a domain
 * or via a route that does not already include a valid brand key.
 */
export const resolveBrandKey = (pathname: string, hostname?: string | null): BrandKey => {
  const routeBrandKey = getBrandKeyFromPathname(pathname);
  if (routeBrandKey) {
    return routeBrandKey;
  }

  if (hostname) {
    const normalizedHostname = hostname.replace(/^www\./, '').toLowerCase();
    const matchedBrand = Object.values(BRAND_CONFIGS).find(
      (brandConfig) => brandConfig.domain === normalizedHostname,
    );

    if (matchedBrand) {
      return matchedBrand.key;
    }
  }

  return DEFAULT_BRAND_KEY;
};

/**
 * Convert a legacy unprefixed path into the default brand namespace.
 * Why this matters: it keeps old links functional while the app transitions to
 * the new brand-prefixed routing model.
 */
export const toDefaultBrandPath = (pathname: string): string =>
  getBrandPath(DEFAULT_BRAND_KEY, getBrandRelativePathFromPathname(pathname));

export const getBrandDomainLabel = (brandKey: BrandKey): string => getBrandConfig(brandKey).domain;
